import { getCountryCallingCode } from "libphonenumber-js";

const DEFAULT_COUNTRY_ISO2 = "in";
const DEFAULT_DIAL_CODE = "91";
const IP_COUNTRY_STORAGE_KEY = "ipCountryDefault";
const IP_COUNTRY_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const DEFAULT_LOOKUP_URL = "https://ipapi.co/json/";

let ipCountryRequest = null;

export const normalizeCountryIso2 = (value = "") => {
  const normalizedValue = String(value || "").trim().slice(0, 2).toLowerCase();
  return /^[a-z]{2}$/.test(normalizedValue) ? normalizedValue : DEFAULT_COUNTRY_ISO2;
};

export const normalizeCountryCallingCode = (value = "", { withPlus = false } = {}) => {
  const normalizedCode = String(value || "").replace(/[^\d]/g, "");
  const dialCode = normalizedCode || DEFAULT_DIAL_CODE;
  return withPlus ? `+${dialCode}` : dialCode;
};

const getDialCodeForCountry = (countryIso2 = DEFAULT_COUNTRY_ISO2) => {
  try {
    return getCountryCallingCode(String(countryIso2).toUpperCase());
  } catch {
    return DEFAULT_DIAL_CODE;
  }
};

const normalizeIpCountryData = (data = {}) => {
  const countryIso2 = normalizeCountryIso2(
    data.countryIso2 || data.country_code || data.countryCode || data.country || data.country_code2
  );
  const dialCode = normalizeCountryCallingCode(
    data.dialCode || data.country_calling_code || data.calling_code || getDialCodeForCountry(countryIso2)
  );

  return {
    countryIso2,
    countryCode: countryIso2.toUpperCase(),
    dialCode,
    dialCodeWithPlus: `+${dialCode}`,
    updatedAt: Date.now(),
  };
};

const readStoredIpCountry = () => {
  if (typeof window === "undefined") return null;

  try {
    const storedValue = window.localStorage?.getItem(IP_COUNTRY_STORAGE_KEY);
    if (!storedValue) return null;

    const parsedValue = JSON.parse(storedValue);
    if (!parsedValue?.updatedAt || Date.now() - parsedValue.updatedAt > IP_COUNTRY_CACHE_TTL_MS) {
      return null;
    }

    return normalizeIpCountryData(parsedValue);
  } catch {
    return null;
  }
};

const writeStoredIpCountry = (ipCountryData) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage?.setItem(IP_COUNTRY_STORAGE_KEY, JSON.stringify(ipCountryData));
  } catch {
    // Ignore storage failures.
  }
};

export const getDefaultIpCountry = () => (
  readStoredIpCountry() || normalizeIpCountryData({ countryIso2: DEFAULT_COUNTRY_ISO2, dialCode: DEFAULT_DIAL_CODE })
);

export const resolveIpCountryDefault = async ({ force = false } = {}) => {
  if (typeof window === "undefined" || typeof fetch !== "function") {
    return getDefaultIpCountry();
  }

  const storedValue = readStoredIpCountry();
  if (!force && storedValue) return storedValue;

  if (ipCountryRequest) return ipCountryRequest;

  const lookupUrl = process.env.REACT_APP_IP_COUNTRY_LOOKUP_URL || DEFAULT_LOOKUP_URL;

  ipCountryRequest = fetch(lookupUrl, { cache: "no-store" })
    .then((response) => {
      if (!response.ok) throw new Error("Unable to resolve IP country");
      return response.json();
    })
    .then((data) => {
      const ipCountryData = normalizeIpCountryData(data);
      writeStoredIpCountry(ipCountryData);
      return ipCountryData;
    })
    .catch(() => getDefaultIpCountry())
    .finally(() => {
      ipCountryRequest = null;
    });

  return ipCountryRequest;
};
